import { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { Loader, FloatingInput } from "../../Components/Common";
import * as UserService from "../../Services/userService.js";
import { ROLE } from "../../Constants/Strings.js";
import { VALIDATION_MESSAGES } from "./constant.js";
import { FaCamera } from "react-icons/fa";
import getInitials from "../../Utils/getInitials.js";
import { extractErrorMessage } from "../../Utils/extractErrorMessage.js";

// Validation schema
const validationSchema = Yup.object().shape({
  first_name: Yup.string().required(VALIDATION_MESSAGES.FIRST_NAME_REQUIRED),
  last_name: Yup.string().required(VALIDATION_MESSAGES.LAST_NAME_REQUIRED),
  mobile: Yup.string()
    .matches(/^\d{10}$/, VALIDATION_MESSAGES.MOBILE_VALIDATION_MESSAGE)
    .required(VALIDATION_MESSAGES.MOBILE_NUMBER_REQUIRED),
  email: Yup.string()
    .email(VALIDATION_MESSAGES.INVALID_MAIL)
    .required(VALIDATION_MESSAGES.EMAIL_REQUIRED),
});

const Profile = () => {
  const [userInitialValues, setUserInitialValues] = useState({
    first_name: "",
    last_name: "",
    role: "",
    email: "",
    mobile: "",
    profile_photo: "",
  });
  const [userApiLoading, setUserApiLoading] = useState(false);

  const [photoFile, setPhotoFile] = useState(null);
  const [photoUpdated, setPhotoUpdated] = useState(false);
  const [originalPhoto, setOriginalPhoto] = useState("");
  const [disableSave, setDisableSave] = useState(false);

  // Local state for form values
  const [formValues, setFormValues] = useState({
    ...userInitialValues,
  });

  // Update form values whenever userInitialValues changes
  useEffect(() => {
    if (userInitialValues && Object.keys(userInitialValues).length > 0) {
      setFormValues({
        first_name: userInitialValues.first_name || "",
        last_name: userInitialValues.last_name || "",
        role: userInitialValues.role || "",
        email: userInitialValues.email || "",
        mobile: userInitialValues.mobile || "",
        profile_photo: userInitialValues.profile_photo || "",
      });
    }
  }, [userInitialValues]);

  // For preview
  const [previewUrl, setPreviewUrl] = useState("");
  useEffect(() => {
    if (photoFile) {
      const objectUrl = URL.createObjectURL(photoFile);
      setPreviewUrl(objectUrl);

      return () => URL.revokeObjectURL(objectUrl);
    } else {
      setPreviewUrl(formValues.profile_photo || "");
    }
  }, [photoFile, formValues.profile_photo]);

  // Fetch user data
  const fetchUserDetails = async () => {
    try {
      setUserApiLoading(true);
      const response = await UserService.getUser();
      if (response.status === 200) {
        const apiRole = response.data?.role;
        const roleLabel =
          Object.values(ROLE).find((r) => r.value === apiRole)?.label ||
          apiRole;

        setUserInitialValues((prev) => ({
          ...prev,
          ...response.data,
          role: roleLabel,
        }));
        setOriginalPhoto(response.data?.profile_photo || "");
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || VALIDATION_MESSAGES.ERROR_UPDATE_USER
      toast.error(msg);
    } finally {
      setUserApiLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, []);

  // Submit handler
  const handleSubmit = async (values, { resetForm }) => {
    try {
      const form = new FormData();
      form.append("first_name", values.first_name || "");
      form.append("last_name", values.last_name || "");
      form.append("email", values.email || "");

      if (photoFile) form.append("profile_photo", photoFile);

      const response = await UserService.updateLoggedInUser(form, true);

      if (response.status === 200) {
        toast.success(VALIDATION_MESSAGES.USER_UPDATED);

        const updatedData = {
          ...values,
          profile_photo:
            response.data?.profile_photo || formValues.profile_photo,
        };

        // Update state so UI reflects immediately
        setFormValues(updatedData);
        resetForm({ values: updatedData });

        if (response.data?.profile_photo) {
          setPreviewUrl(response.data.profile_photo);
          setOriginalPhoto(response.data.profile_photo);
        } else if (photoFile) {
          const objectUrl = URL.createObjectURL(photoFile);
          setPreviewUrl(objectUrl);
          setOriginalPhoto(objectUrl);
        }

        setPhotoFile(null);
        setPhotoUpdated(false);
        setDisableSave(false);
      } else {
        toast.error(VALIDATION_MESSAGES.FAILED_UPDATE_USER);
        setPreviewUrl(originalPhoto);
        setPhotoFile(null);
        setPhotoUpdated(false);
        setDisableSave(true);
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || VALIDATION_MESSAGES.ERROR_UPDATE_USER;
      toast.error(msg);
      setPreviewUrl(originalPhoto);
      setPhotoFile(null);
      setPhotoUpdated(false);
      setFormValues(userInitialValues);
      setDisableSave(true);
    }
  };

  return (
    <div className="p-2">
      <Formik
        initialValues={formValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, dirty, setFieldValue }) => (
          <Form className="relative max-w-xl p-4 mx-auto bg-white shadow-md rounded-2xl">
            <h2 className="mb-2 text-2xl font-bold text-center text-primary">
              Profile
            </h2>

            {userApiLoading ? (
              <div className="flex justify-center py-10">
                <Loader size={4} />
              </div>
            ) : (
              <div className="space-y-2">
                {/* Profile Image */}
                <div className="relative flex justify-center">
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Profile"
                      className="object-cover border-4 border-white rounded-full shadow-md w-24 h-24 sm:w-28 sm:h-28 mb-2"
                    />
                  ) : (
                    <div
                      className="flex items-center justify-center text-xl sm:text-2xl font-semibold text-white border-4 border-white rounded-full shadow-md w-24 h-24 sm:w-28 sm:h-28"
                      style={{ background: "#64748b" }}
                    >
                      {getInitials(
                        `${formValues.first_name} ${formValues.last_name}`
                      )}
                    </div>
                  )}

                  <label className="absolute bottom-0 right-[calc(50%-56px)] bg-primary p-2 rounded-full cursor-pointer shadow-md hover:bg-secondary transition-colors duration-200">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onClick={(e) => (e.target.value = null)}
                      onChange={(e) => {
                        const f = e.currentTarget.files?.[0];
                        if (f) {
                          setPhotoFile(f);
                          setFieldValue("profile_photo", f);
                          setPhotoUpdated(true);
                        }
                      }}
                    />
                    <FaCamera className="text-lg text-white" />
                  </label>
                </div>

                {/* Name row */}
                <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0">
                  <div className="flex-1">
                    <FloatingInput name="first_name" label="First Name *" />
                  </div>
                  <div className="flex-1">
                    <FloatingInput name="last_name" label="Last Name *" />
                  </div>
                </div>

                {/* Other fields */}
                <div className="space-y-6">
                  <FloatingInput name="email" label="Email *" type="email" />

                  <FloatingInput
                    name="mobile"
                    label="Mobile No. *"
                    type="tel"
                    disabled={true}
                  />

                  <FloatingInput name="role" label="Role *" disabled={true} />
                </div>

                {/* Submit button */}
                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    disabled={disableSave || (!dirty && !photoUpdated) || isSubmitting}
                    className={`font-semibold py-2 px-6 rounded ${
                      disableSave || (!dirty && !photoUpdated) || isSubmitting
                        ? "bg-gray-400 text-white cursor-not-allowed"
                        : "bg-primary hover:bg-secondary text-white"
                    }`}
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default Profile;
